const TOKENS = [
	{ token: '{yyy}', key: 'yearShort', digits: 3 },
	{ token: '{yyyy}', key: 'year', digits: 4 },
	{ token: '{yy}', key: 'yearShort', digits: 2 },
	{ token: '{MM}', key: 'month', digits: 2 },
	{ token: '{M}', key: 'month', digits: 1 },
	{ token: '{ddd}', key: 'day', digits: 3 },
	{ token: '{dd}', key: 'day', digits: 2 },
	{ token: '{d}', key: 'day', digits: 1 },
	{ token: '{hhh}', key: 'hours', digits: 3 },
	{ token: '{hh}', key: 'hours', digits: 2 },
	{ token: '{h}', key: 'hours', digits: 1 },
	{ token: '{mmm}', key: 'milliseconds', digits: 3 },
	{ token: '{sss}', key: 'milliseconds', digits: 3 },
	{ token: '{mm}', key: 'minutes', digits: 2 },
	{ token: '{m}', key: 'minutes', digits: 1 },
	{ token: '{ss}', key: 'seconds', digits: 2 },
	{ token: '{s}', key: 'seconds', digits: 1 },
] as const;

function padValue(value: number, digits: number): string {
	return String(value).padStart(digits, '0').slice(-digits);
}

function getTimeComponents(timeMs: number, useMoscowTimezone: boolean) {
	if (useMoscowTimezone) {
		const moscowOffsetMs = 3 * 60 * 60 * 1000;
		const d = new Date(timeMs + moscowOffsetMs);

		return {
			year: d.getUTCFullYear(),
			yearShort: d.getUTCFullYear() % 100,
			month: d.getUTCMonth() + 1,
			day: d.getUTCDate(),
			hours: d.getUTCHours(),
			minutes: d.getUTCMinutes(),
			seconds: d.getUTCSeconds(),
			milliseconds: d.getUTCMilliseconds(),
		};
	}

	const totalSeconds = Math.floor(timeMs / 1000);
	const milliseconds = timeMs % 1000;
	const seconds = totalSeconds % 60;
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const hours = Math.floor(totalSeconds / 3600);

	return { year: 0, yearShort: 0, month: 0, day: 0, hours, minutes, seconds, milliseconds };
}

/**
 * Форматирует время в читаемый формат.
 *
 * @param timeMs - Время в миллисекундах (абсолютный timestamp или duration)
 * @param format - Формат строки (по умолчанию '{mm}:{ss}')
 * @param options.useMoscowTimezone - Если true, обрабатывает как абсолютный timestamp с UTC+3 сдвигом
 *
 * Поддерживаемые токены:
 *   Дата (только с useMoscowTimezone: true):
 *     {d}, {dd}     - день (без/с ведущим нулём)
 *     {M}, {MM}     - месяц
 *     {yy}, {yyyy}  - год (2/4 цифры)
 *   Время:
 *     {h}, {hh}, {hhh} - часы
 *     {m}, {mm}        - минуты
 *     {s}, {ss}        - секунды
 *     {sss}, {mmm}     - миллисекунды
 *
 * Примеры:
 *   formatTime(300000)                               -> "05:00"
 *   formatTime(300000, '{mm}:{ss}')                   -> "05:00"
 *   formatTime(3665000, '{h}:{mm}:{ss}')              -> "1:01:05"
 *   formatTime(300000, 'Time: {m}m {s}s')             -> "Time: 5m 0s"
 *   formatTime(Date.now(), '{hh}:{mm}', { useMoscowTimezone: true }) -> "14:30"
 *   formatTime(timestamp, '{dd}/{MM}/{yyyy}', { useMoscowTimezone: true }) -> "10/05/2026"
 *   formatTime(timestamp, '{dd}.{MM}.{yy}', { useMoscowTimezone: true }) -> "10.05.26"
 */
export function formatTime(
	timeMs: number,
	format: string = '{mm}:{ss}',
	options: { useMoscowTimezone?: boolean } = {},
): string {
	if (timeMs === Infinity || timeMs < 0) {
		return '';
	}

	const useMoscowTimezone = Boolean(options.useMoscowTimezone);
	const values = getTimeComponents(timeMs, useMoscowTimezone);

	let result = format;
	for (const { token, key, digits } of TOKENS) {
		if (result.includes(token)) {
			result = result.split(token).join(padValue(values[key], digits));
		}
	}

	return result;
}
